import React from 'react';
import {Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {DetailedFieldResult} from '../../../../store/models/DuplicateCheck';
import {ADDRESS_FIELDS, FIELD_LABELS, SKIPPED_LABELS} from './constants';
import {ScoreBar} from './shared';

const useStyles = makeStyles((theme) => ({
    hint: {
        display: 'block',
        marginBottom: theme.spacing(1),
    },
    skippedRow: {
        opacity: 0.5,
    },
    summaryRow: {
        backgroundColor: theme.palette.action.hover,
    },
    addressSectionCell: {
        backgroundColor: theme.palette.grey[100],
        padding: theme.spacing(0.5, 2),
        borderBottom: 'none',
    },
}));

export default function FieldDetailTable({fields}: {fields: DetailedFieldResult[]}) {
    const classes = useStyles();
    const totalContribution = fields.reduce((sum, f) => sum + (f.weightedContribution ?? 0), 0);

    const comparedFields = fields.filter((f) => f.status === 'compared');
    const sumOfComparedWeights = comparedFields.reduce((sum, f) => sum + f.weight, 0);
    const allWeightsUsed = Math.abs(sumOfComparedWeights - fields.reduce((s, f) => s + f.weight, 0)) < 0.001;

    const effectiveWeightPct = (f: DetailedFieldResult): number | null => {
        if (f.status !== 'compared' || sumOfComparedWeights === 0) return null;
        return Math.round((f.weight / sumOfComparedWeights) * 100);
    };

    return (
        <>
            {!allWeightsUsed && (
                <Typography variant="caption" color="textSecondary" className={classes.hint}>
                    Es wurden nur {comparedFields.length} von {fields.length} Feld{fields.length !== 1 ? 'ern' : ''} geprüft.
                    Die Gewichtung der geprüften Felder wurde entsprechend angepasst.
                </Typography>
            )}
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Feld</TableCell>
                        <TableCell>Eingabe</TableCell>
                        <TableCell>Kandidat</TableCell>
                        <TableCell>Score</TableCell>
                        <TableCell>Gewicht</TableCell>
                        <TableCell>Beitrag</TableCell>
                        <TableCell>Status</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {fields.map((f, i) => {
                        const isSkipped = f.status.startsWith('skipped');
                        const configPct = Math.round(f.weight * 100);
                        const effPct = effectiveWeightPct(f);
                        const showEffective = !allWeightsUsed && effPct != null && effPct !== configPct;
                        const isFirstAddressField = ADDRESS_FIELDS.has(f.fieldName)
                            && (i === 0 || !ADDRESS_FIELDS.has(fields[i - 1].fieldName));

                        return (
                            <React.Fragment key={f.fieldName}>
                                {isFirstAddressField && (
                                    <TableRow>
                                        <TableCell colSpan={7} className={classes.addressSectionCell}>
                                            <Typography variant="caption" color="textSecondary"><b>Adresse</b></Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                <TableRow className={isSkipped ? classes.skippedRow : undefined}>
                                    <TableCell>{FIELD_LABELS[f.fieldName] ?? f.fieldName}</TableCell>
                                    <TableCell>
                                        {f.inputOriginal && <div>{f.inputOriginal}</div>}
                                        {f.inputNormalized && f.inputNormalized !== f.inputOriginal && (
                                            <Typography variant="caption" color="textSecondary">
                                                → {f.inputNormalized}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {f.candidateOriginal && <div>{f.candidateOriginal}</div>}
                                        {f.candidateNormalized && f.candidateNormalized !== f.candidateOriginal && (
                                            <Typography variant="caption" color="textSecondary">
                                                → {f.candidateNormalized}
                                            </Typography>
                                        )}
                                        {f.distanceMeters != null && (
                                            <Typography variant="caption" color="textSecondary">
                                                {' '}({f.distanceMeters} m)
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell><ScoreBar score={f.fieldScore}/></TableCell>
                                    <TableCell>
                                        {isSkipped
                                            ? <Typography variant="caption" color="textSecondary">—</Typography>
                                            : showEffective
                                                ? <>{configPct} % <Typography variant="caption" color="textSecondary">(effektiv {effPct} %)</Typography></>
                                                : `${configPct} %`
                                        }
                                    </TableCell>
                                    <TableCell>
                                        {f.weightedContribution != null
                                            ? `${Math.round(f.weightedContribution * 100)} %`
                                            : <Typography variant="caption" color="textSecondary">—</Typography>
                                        }
                                    </TableCell>
                                    <TableCell>
                                        {isSkipped
                                            ? <Typography variant="caption" color="textSecondary">
                                                {SKIPPED_LABELS[f.status] ?? 'Übersprungen'}
                                            </Typography>
                                            : <Typography variant="caption">Verglichen</Typography>
                                        }
                                    </TableCell>
                                </TableRow>
                            </React.Fragment>
                        );
                    })}
                    <TableRow className={classes.summaryRow}>
                        <TableCell colSpan={5}>
                            <Typography variant="caption"><b>Gesamt-Score</b></Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="caption"><b>{Math.round(totalContribution * 100)} %</b></Typography>
                        </TableCell>
                        <TableCell/>
                    </TableRow>
                </TableBody>
            </Table>
        </>
    );
}
